import { Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { Loading } from '../../components/common/Loading/Loading';
import { Pagination } from '../../components/common/Pagination';
import { ROUTES } from '../../constants/routes';
import { useUserStatus } from '../../hooks/useUserStatus';
import { usePaginationState } from '../../hooks/usePaginationState';
import { getUrlForRequest } from './renderHelpers';
import { IHistoryResponse } from './types';
import { Card } from '../../components/common/Card';
import { CardEnum } from '../../components/common/Card/types';
import { OcppService } from '../../services/ocpp/OcppService';
import { TitleWithBackButton } from '../../components/common/TitleWithBackButton/TitleWithBackTutton';

const ocppService = new OcppService();

export const UserHistory = () => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<IHistoryResponse | null>(null);
  const { page, rowsPerPage, setPage, setRowsPerPage, resetPage } = usePaginationState();

  const location = useLocation();
  const { isUserPhoneConfirmed } = useUserStatus();
  const { t } = useTranslation();

  const lastPath = useMemo(
    () => location.pathname.split('/').pop() || '',
    [location.pathname],
  );

  const isTransactionsPage = useMemo(
    () => lastPath === ROUTES.CAB_TRANSACTIONS,
    [lastPath],
  );

  const isEmpty = useMemo(
    () => !history?.data?.length,
    [history?.data?.length],
  );

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await ocppService.getUserSessionOrTransactions(
        getUrlForRequest(lastPath),
        page + 1,
        rowsPerPage,
      );

      setHistory(response);
    } catch (error) {
      Sentry.captureException({
        message: '[ocppService][getUserSessionOrTransactions]',
        error,
      });
    } finally {
      setLoading(false);
    }
  }, [lastPath, page, rowsPerPage]);

  useEffect(() => {
    resetPage();
  }, [location.pathname, resetPage]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchTransactions();

    return () => controller.abort();
  }, [fetchTransactions]);

  if (isUserPhoneConfirmed === false) return <Navigate to="/cabinet" />;
  if (isUserPhoneConfirmed === null || loading) return <Loading />;

  const emptyText = isTransactionsPage
    ? t('userHistoryPage.emptyTransactions')
    : t('userHistoryPage.emptySessions');

  const titleText = isTransactionsPage
    ? t('userHistoryPage.titleTransactions')
    : t('userHistoryPage.titleSessions');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCards = (item: any, index: number) => {
    return (
      <Card
        key={index}
        type={
          isTransactionsPage
            ? CardEnum.User_transaction_card
            : CardEnum.User_Session_card
        }
        data={item}
      />
    );
  };

  return (
    <Stack spacing={3} sx={containerStyles} textAlign="center">
      <TitleWithBackButton title={titleText} position="center" />
      {isEmpty ? (
        <Typography variant="body1" color="common.white">
          {emptyText}
        </Typography>
      ) : (
        <>
          <Stack spacing={2} sx={{ overflow: 'auto' }}>
            {history?.data.map(renderCards)}
          </Stack>
          <Pagination
            tone="dark"
            total={history?.total ?? 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </>
      )}
    </Stack>
  );
};

const containerStyles = {
  padding: '14px 22px',
  maxWidth: 400,
  width: '100%',
};
